import React, { useEffect, useState, useCallback } from 'react';
import { useSocket } from '../../context/SocketContext';
import { QuorumManagerView } from '../../types/assembly';

interface QuorumStatusProps {
  assemblyId: string;
  className?: string;
  showDetails?: boolean;
  assemblyStatus?: string;
  externalQuorumStatus?: QuorumManagerView | null;
  disableApiSync?: boolean;
}

interface QuorumStatusData {
  quorumRequired: number;
  quorumAchieved: number;
  attendeeCoefficient: number;
  totalCoefficient: number;
  isQuorumMet: boolean;
  representedApartmentsCount?: number;
  missingForQuorum?: number;
}

const QuorumStatusComponent: React.FC<QuorumStatusProps> = ({ 
  assemblyId, 
  className = '',
  showDetails = true,
  assemblyStatus,
  externalQuorumStatus = null,
  disableApiSync = false
}) => {
  const { quorumStatus: socketQuorumStatus } = useSocket();
  const [localQuorumStatus, setLocalQuorumStatus] = useState<QuorumStatusData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const QUORUM_SYNC_INTERVAL_VISIBLE_MS = 10000;
  const QUORUM_SYNC_INTERVAL_HIDDEN_MS = 30000;
  const isAssemblyActive = assemblyStatus === 'in_progress';

  useEffect(() => {
    if (!externalQuorumStatus) return;

    setLocalQuorumStatus({
      quorumRequired: Number(externalQuorumStatus.quorumRequired || 0),
      quorumAchieved: Number(externalQuorumStatus.quorumAchieved || 0),
      attendeeCoefficient: Number(externalQuorumStatus.attendeeCoefficient || 0),
      totalCoefficient: Number(externalQuorumStatus.totalCoefficient || 0),
      isQuorumMet: Boolean(externalQuorumStatus.isQuorumMet),
      representedApartmentsCount: Number(externalQuorumStatus.representedApartmentsCount || 0),
      missingForQuorum: externalQuorumStatus.missingForQuorum,
    });
    setIsLoading(false);
  }, [externalQuorumStatus]);

  // Cargar estado inicial del quórum
  useEffect(() => {
    if (disableApiSync) {
      setIsLoading(false);
      return;
    }

    const fetchQuorumStatus = async () => {
      try {
        setIsLoading(true);
        
        // ✅ Usar 'accessToken' en lugar de 'token'
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          console.warn('⚠️ No hay token disponible para cargar quórum');
          setIsLoading(false);
          return;
        }
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/quorum/assembly/${assemblyId}/status/manager-view`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // El backend devuelve { success: true, quorumStatus: {...} }
          const quorumData = data.quorumStatus || data;
          
          // Normalizar los datos (convertir strings a números)
          const normalizedData: QuorumStatusData = {
            quorumRequired: parseFloat(quorumData.quorumRequired) || 0,
            quorumAchieved: parseFloat(quorumData.quorumAchieved) || 0,
            attendeeCoefficient: parseFloat(quorumData.attendeeCoefficient) || 0,
            totalCoefficient: parseFloat(quorumData.totalCoefficient) || 0,
            isQuorumMet: quorumData.isQuorumMet,
            representedApartmentsCount: parseInt(quorumData.representedApartmentsCount, 10) || 0,
            missingForQuorum: quorumData.missingForQuorum ? parseFloat(quorumData.missingForQuorum) : undefined
          };
          
          setLocalQuorumStatus(normalizedData);
        } else if (response.status === 401) {
          console.error('❌ Error de autenticación (401) al cargar quórum');
        } else {
          console.error('❌ Error al cargar quórum:', response.status);
        }
      } catch (error) {
        console.error('❌ Error al cargar estado del quórum:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (assemblyId) {
      fetchQuorumStatus();
    }
  }, [assemblyId, disableApiSync]);

  // Actualizar estado local cuando llega un evento de Socket.IO
  useEffect(() => {
    if (socketQuorumStatus) {
      // Normalizar los datos por si vienen como strings
      const normalizedData: QuorumStatusData = {
        quorumRequired: typeof socketQuorumStatus.quorumRequired === 'string' 
          ? parseFloat(socketQuorumStatus.quorumRequired) 
          : socketQuorumStatus.quorumRequired,
        quorumAchieved: typeof socketQuorumStatus.quorumAchieved === 'string'
          ? parseFloat(socketQuorumStatus.quorumAchieved)
          : socketQuorumStatus.quorumAchieved,
        totalCoefficient: typeof socketQuorumStatus.totalCoefficient === 'string'
          ? parseFloat(socketQuorumStatus.totalCoefficient)
          : socketQuorumStatus.totalCoefficient,
        attendeeCoefficient: typeof socketQuorumStatus.attendeeCoefficient === 'string'
          ? parseFloat(socketQuorumStatus.attendeeCoefficient)
          : socketQuorumStatus.attendeeCoefficient,
        isQuorumMet: socketQuorumStatus.isQuorumMet,
        representedApartmentsCount: Number(socketQuorumStatus.representedApartmentsCount || 0),
        missingForQuorum: socketQuorumStatus.missingForQuorum
      };
      
      setLocalQuorumStatus(normalizedData);
    }
  }, [socketQuorumStatus]);

  // Función para recargar estado del quórum desde el backend (useCallback para estabilizar)
  const reloadQuorumStatus = useCallback(async () => {
    if (disableApiSync) {
      return false;
    }

    try {
      const accessToken = localStorage.getItem('accessToken');
      
      if (!accessToken || !assemblyId) {
        console.warn('No hay token o assemblyId para recargar quórum');
        return false;
      }
      
      const url = `${process.env.REACT_APP_API_URL}/quorum/assembly/${assemblyId}/status/manager-view`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const quorumData = data.quorumStatus || data;
        
        const normalizedData: QuorumStatusData = {
          quorumRequired: parseFloat(quorumData.quorumRequired) || 0,
          quorumAchieved: parseFloat(quorumData.quorumAchieved) || 0,
          attendeeCoefficient: parseFloat(quorumData.attendeeCoefficient) || 0,
          totalCoefficient: parseFloat(quorumData.totalCoefficient) || 0,
          isQuorumMet: quorumData.isQuorumMet,
          representedApartmentsCount: parseInt(quorumData.representedApartmentsCount, 10) || 0,
          missingForQuorum: quorumData.missingForQuorum ? parseFloat(quorumData.missingForQuorum) : undefined
        };
        
        setLocalQuorumStatus(normalizedData);
        return true;
      } else {
        console.error('Error al recargar quórum:', response.status);
      }
    } catch (error) {
      console.error('Error en reloadQuorumStatus:', error);
    }
    return false;
  }, [assemblyId, disableApiSync]);

  // Polling adaptativo: solo cuando la asamblea está activa.
  // Visible: 10s | Background: 30s
  useEffect(() => {
    if (!assemblyId || !isAssemblyActive || disableApiSync) return;

    let intervalId: number | null = null;

    const restartPolling = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }

      const intervalMs = document.visibilityState === 'visible'
        ? QUORUM_SYNC_INTERVAL_VISIBLE_MS
        : QUORUM_SYNC_INTERVAL_HIDDEN_MS;

      intervalId = window.setInterval(() => {
        reloadQuorumStatus();
      }, intervalMs);
    };

    const handleVisibilityChange = () => {
      // Al volver visible, forzamos refresh inmediato y reconfiguramos frecuencia.
      if (document.visibilityState === 'visible') {
        reloadQuorumStatus();
      }
      restartPolling();
    };

    restartPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [assemblyId, isAssemblyActive, disableApiSync, reloadQuorumStatus]);

  // Forzar recarga al volver foco o visibilidad, clave para pruebas en móviles.
  useEffect(() => {
    if (!assemblyId || !isAssemblyActive || disableApiSync) return;

    const handleVisibilityOrFocus = () => {
      const isVisible = document.visibilityState === 'visible';
      if (isVisible) {
        reloadQuorumStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [assemblyId, isAssemblyActive, disableApiSync, reloadQuorumStatus]);

  // Resetear quórum cuando la asamblea se completa
  useEffect(() => {
    if (assemblyStatus === 'completed') {
      console.log('✅ [QuorumStatus] Asamblea completada - Reseteando estado del quórum');
      setLocalQuorumStatus(null);
    }
  }, [assemblyStatus]);

  // Escuchar cambios en participantes elegibles por delegaciones
  useEffect(() => {
    const handleEligibleParticipantsChanged = (event: Event) => {
      reloadQuorumStatus();
    };

    window.addEventListener('eligible:participants-changed', handleEligibleParticipantsChanged as EventListener);
    return () => {
      window.removeEventListener('eligible:participants-changed', handleEligibleParticipantsChanged as EventListener);
    };
  }, [assemblyId, reloadQuorumStatus]);

  const quorumStatus = localQuorumStatus;

  if (isLoading || !quorumStatus) {
    return (
      <div className={`rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm ${className}`}>
        <div className="flex items-center gap-2.5">
          <svg className="h-5 w-5 animate-spin text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span className="text-sm font-medium text-slate-600">Cargando información de quórum...</span>
        </div>
      </div>
    );
  }

  const isQuorumMet = quorumStatus.isQuorumMet;
  // quorumAchieved ya viene como porcentaje del backend (ej: 45.5 = 45.5%)
  // quorumRequired también es porcentaje (ej: 50 = 50%)
  const achievedPercentage = Math.round(quorumStatus.quorumAchieved);
  const percentage = Math.round(Math.min(achievedPercentage, 100));

  const getStatusColor = () => {
    if (isQuorumMet) return 'text-green-600';
    if (percentage >= 80) return 'text-yellow-600';
    if (percentage >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getProgressColor = () => {
    if (isQuorumMet) return 'bg-green-500';
    if (percentage >= 80) return 'bg-yellow-500';
    if (percentage >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const StatusIcon = () => {
    if (isQuorumMet) {
      return (
        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    } else {
      return (
        <svg className={`w-6 h-6 ${getStatusColor()}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    }
  };

  const statusPillClassName = isQuorumMet
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : 'border-amber-200 bg-amber-50 text-amber-700';

  const statusMessageClassName = isQuorumMet
    ? 'border-emerald-200 bg-emerald-50/70 text-emerald-800'
    : 'border-amber-200 bg-amber-50/70 text-amber-900';

  const shellClassName = isQuorumMet
    ? 'border-emerald-200/80 bg-gradient-to-br from-emerald-50/70 via-white to-slate-50'
    : 'border-amber-200/80 bg-gradient-to-br from-amber-50/75 via-white to-slate-50';

  const accentClassName = isQuorumMet ? 'bg-emerald-500' : 'bg-amber-500';

  const iconWrapClassName = isQuorumMet
    ? 'border-emerald-200 bg-white text-emerald-700'
    : 'border-amber-200 bg-white text-amber-700';

  return (
    <div className={`relative overflow-hidden rounded-2xl border shadow-sm ${shellClassName} ${className}`}>
      <div className={`absolute inset-y-0 left-0 w-1 ${accentClassName}`} />
      <div className="p-4 md:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 rounded-xl border p-2 shadow-sm ${iconWrapClassName}`}>
              <StatusIcon />
            </div>
            <div>
              <h3 className={`text-base font-semibold ${getStatusColor()}`}>
                {isQuorumMet ? 'Quórum Alcanzado' : 'Quórum Pendiente'}
              </h3>
              <p className="mt-0.5 text-sm text-slate-600">
                {achievedPercentage}% de participación
              </p>
            </div>
          </div>
          <span className={`inline-flex w-fit items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${statusPillClassName}`}>
            {isQuorumMet ? 'Votación habilitada' : 'En espera'}
          </span>
        </div>

        <div className="mb-4 rounded-xl border border-slate-200/80 bg-white/80 p-3">
          <div className="mb-1 flex justify-between text-sm text-slate-600">
            <span>Progreso del quórum</span>
            <span className="font-semibold text-slate-700">{percentage}%</span>
          </div>
          <div className="relative h-3 w-full rounded-full bg-slate-200">
            <div
              className={`h-3 rounded-full transition-all duration-500 ease-out ${getProgressColor()}`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            ></div>
            <div
              className="absolute top-0 h-5 w-0.5 -translate-y-1 rounded-full bg-slate-700"
              style={{ left: `${Math.round(quorumStatus.quorumRequired)}%` }}
              title={`Quórum requerido: ${Math.round(quorumStatus.quorumRequired)}%`}
            ></div>
          </div>
          <div className="mt-1 flex justify-between text-xs text-slate-500">
            <span>0%</span>
            <span className="font-medium text-slate-700">Mínimo: {Math.round(quorumStatus.quorumRequired)}%</span>
            <span>100%</span>
          </div>
        </div>

        {showDetails && (
          <div className="space-y-3">

{/* Información de coeficientes (importante para votaciones) */}
            <div className="rounded-xl border border-slate-200/80 bg-white/80 p-3">
              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 text-center">
                  <span className="text-slate-600">Coef. Presente:</span>
                  <span className="ml-2 text-lg font-bold text-emerald-600">
                    {quorumStatus.attendeeCoefficient?.toFixed(10) || '0.00'} %
                  </span>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 text-center">
                  <span className="text-slate-600">Apartamentos representados:</span>
                  <span className="ml-2 text-lg font-bold text-blue-600">
                    {quorumStatus.representedApartmentsCount ?? 0}
                  </span>
                </div>
              </div>

              {quorumStatus.totalCoefficient === 0 && (
                <div className="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 p-2">
                  <p className="text-xs text-yellow-800">
                  ⚠️ Los coeficientes no están configurados. El quórum se calculará cuando se asignen coeficientes a los propietarios en el perfil de usuarios.
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-blue-200/90 bg-blue-50/80 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700">
                  Quórum mínimo requerido:
                </span>
                <span className="text-xl font-bold text-blue-700">
                  {Math.round(quorumStatus.quorumRequired)}%
                </span>
              </div>
              {!isQuorumMet && quorumStatus.missingForQuorum && (
                <div className="mt-1.5 text-xs text-slate-600">
                  Se necesitan {quorumStatus.missingForQuorum.toFixed(10)} puntos más de coeficiente
                </div>
              )}
            </div>

            <div className={`rounded-xl border px-3 py-2 text-sm font-medium ${statusMessageClassName}`}>
              {isQuorumMet
                ? '🎉 ¡La asamblea puede proceder con las votaciones!'
                : '⏳ Esperando más participantes para alcanzar el quórum mínimo.'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente compacto para mostrar solo el estado
export const QuorumStatusBadge: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { quorumStatus, isConnected } = useSocket();

  if (!isConnected || !quorumStatus) {
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 ${className}`}>
        <div className="w-2 h-2 bg-gray-400 rounded-full mr-2 animate-pulse"></div>
        Conectando...
      </div>
    );
  }

  const isQuorumMet = quorumStatus.isQuorumMet;
  const percentage = Math.round(quorumStatus.quorumAchieved);

  return (
    <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
      isQuorumMet 
        ? 'bg-green-100 text-green-800' 
        : 'bg-yellow-100 text-yellow-800'
    } ${className}`}>
      <div className={`w-2 h-2 rounded-full mr-2 ${
        isQuorumMet ? 'bg-green-500' : 'bg-yellow-500'
      }`}></div>
      {isQuorumMet ? 'Quórum OK' : `${percentage}% Presente`}
    </div>
  );
};

export default QuorumStatusComponent;
